import { SkyBadge, SkyButton, SkyMenu, SkyText } from "@skypointcloud/skypoint-ui";
import { BREAKABLE_NAME_CLASS, breakableName } from "@/lib/breakableName";
import { useState } from "react";
import { ga4FilterSummary } from "@shared/connectors/ga4Report";
import { targetTableName, type TableNaming } from "@shared/warehouse/targetTableName";
import { SingleCheckbox } from "@/components/ui/SingleCheckbox";
import { AddIcon, CopyIcon, DeleteIcon, EditIcon, MoreActionsIcon, ViewIcon } from "@/lib/icons";
import { instantHoverTitle } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { ZoneCaption } from "@/components/shell/ZoneCaption";
import { dataThroughText, type EntityRow } from "../EntityTable";
import { HIDDEN_REASON, HIDDEN_VALUE } from "./ReportDrawer";

/**
 * #2428: a GA4 connection's Reports, one row per report, which fits a 1280 px screen without
 * sideways scroll (R26): the tick, the report's name with the table it lands in under it, the
 * property, "6 dimensions, 10 metrics" that opens to every name, the filter in a sentence, Data
 * through and the refresh rule ("Replaced by day"), and "Rebuilds on the next import." while the
 * table does not hold the report's current definition. Edit, Duplicate and Remove are in the row's
 * menu; a reader who cannot edit gets View. The list starts empty: GA4 offers no report of its own.
 */

export const REPORTS_EMPTY = "No reports yet. Add a report to choose what to import.";
/** The empty list for a reader who cannot add one (review of #2442): no step they cannot take. */
export const REPORTS_EMPTY_READ_ONLY = "No reports yet. A contributor or admin can add one.";
export const REBUILDS_NEXT_IMPORT = "Rebuilds on the next import.";
export const REPLACED_BY_DAY = "Replaced by day";

export interface ReportListProps {
  rows: EntityRow[];
  selectedCount: number;
  readOnly: boolean;
  naming: TableNaming;
  onToggle: (names: string[], isSelected: boolean) => void;
  onAdd?: () => void;
  onEdit: (name: string) => void;
  onDuplicate: (name: string) => void;
  onRemove: (name: string) => void;
  onView: (name: string) => void;
  /** Pages, for a connection with more reports than one page holds. */
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
}

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? "" : "s"}`;
}

export function ReportList(props: ReportListProps) {
  const { rows, selectedCount, readOnly, naming, page, pageSize, total } = props;
  const zone = useDisplayTz();
  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="flex flex-col gap-3" data-testid="report-list">
      <div className="flex flex-wrap items-center gap-2">
        <SkyBadge appearance="tint" color="subtle" testId="entity-selected-count">
          {selectedCount.toLocaleString()} of {total.toLocaleString()} selected
        </SkyBadge>
        <span className="flex-1" />
        {props.onAdd ? (
          <SkyButton
            appearance="secondary"
            icon={AddIcon}
            testId="entity-add"
            disabled={readOnly}
            onClick={props.onAdd}
          >
            Add report
          </SkyButton>
        ) : null}
      </div>

      {rows.length === 0 && !props.isLoading ? (
        <div
          className="flex flex-col items-start gap-3 rounded-md border border-border p-6"
          data-testid="reports-empty"
        >
          <SkyText as="p" size={300} block>
            {props.onAdd && !readOnly ? REPORTS_EMPTY : REPORTS_EMPTY_READ_ONLY}
          </SkyText>
          {props.onAdd && !readOnly ? (
            <SkyButton
              appearance="primary"
              icon={AddIcon}
              testId="reports-empty-add"
              onClick={props.onAdd}
            >
              Add report
            </SkyButton>
          ) : null}
        </div>
      ) : (
        <ul className="flex flex-col divide-y divide-border rounded-md border border-border">
          {rows.map((row) => (
            <ReportRow
              key={row.name}
              row={row}
              readOnly={readOnly}
              naming={naming}
              zone={zone}
              onToggle={props.onToggle}
              onEdit={props.onEdit}
              onDuplicate={props.onDuplicate}
              onRemove={props.onRemove}
              onView={props.onView}
            />
          ))}
        </ul>
      )}

      {pages > 1 ? (
        <div className="flex items-center gap-2">
          <SkyButton
            size="small"
            appearance="subtle"
            disabled={page <= 1}
            onClick={() => props.onPageChange(page - 1)}
          >
            Previous
          </SkyButton>
          <SkyText size={200} color="muted">
            Page {page} of {pages}
          </SkyText>
          <SkyButton
            size="small"
            appearance="subtle"
            disabled={page >= pages}
            onClick={() => props.onPageChange(page + 1)}
          >
            Next
          </SkyButton>
        </div>
      ) : null}
      {rows.some((row) => row.refreshedThrough) ? (
        <ZoneCaption testId="report-zone-caption" />
      ) : null}
    </div>
  );
}

function ReportRow({
  row,
  readOnly,
  naming,
  zone,
  onToggle,
  onEdit,
  onDuplicate,
  onRemove,
  onView,
}: {
  row: EntityRow;
  readOnly: boolean;
  naming: TableNaming;
  zone: string;
  onToggle: ReportListProps["onToggle"];
  onEdit: ReportListProps["onEdit"];
  onDuplicate: ReportListProps["onDuplicate"];
  onRemove: ReportListProps["onRemove"];
  onView: ReportListProps["onView"];
}) {
  const [expanded, setExpanded] = useState(false);
  const definition = row.definition;
  const withheld = new Set(definition?.withheld ?? []);
  const table = targetTableName({
    ...naming,
    entityName: row.name,
    ...(row.tableNameCase ? { tableNameCase: row.tableNameCase } : {}),
    override: row.targetName ?? null,
  });
  const dimensions = definition?.dimensions ?? [];
  const metrics = definition?.metrics ?? [];
  const filter = withheld.has("dimensionFilter")
    ? HIDDEN_VALUE
    : ga4FilterSummary(definition?.dimensionFilter ?? null);
  const property = withheld.has("propertyId") ? HIDDEN_VALUE : (definition?.propertyId ?? "None");

  const menu = readOnly
    ? [
        {
          key: "view",
          label: "View",
          icon: <ViewIcon className="size-4" />,
          onClick: () => onView(row.name),
        },
      ]
    : [
        {
          key: "edit",
          label: "Edit",
          icon: <EditIcon className="size-4" />,
          onClick: () => onEdit(row.name),
        },
        {
          key: "duplicate",
          label: "Duplicate",
          icon: <CopyIcon className="size-4" />,
          onClick: () => onDuplicate(row.name),
        },
        {
          key: "remove",
          label: "Remove",
          icon: <DeleteIcon className="size-4" />,
          danger: true,
          onClick: () => onRemove(row.name),
        },
      ];

  return (
    <li className="flex gap-3 p-3" data-testid={`report-row-${row.name}`}>
      <div className="pt-0.5">
        {readOnly ? null : (
          <SingleCheckbox
            label={`Import ${row.name}`}
            checked={row.isSelected}
            testId={`report-select-${row.name}`}
            onChange={(checked) => onToggle([row.name], checked)}
          />
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <SkyText
            weight="medium"
            block
            className={BREAKABLE_NAME_CLASS}
            testId={`entity-name-${row.name}`}
          >
            {breakableName(row.name)}
          </SkyText>
          {readOnly ? (
            <SkyText size={200} color="muted" testId={`entity-selected-state-${row.name}`}>
              {row.isSelected ? "Selected" : "Unselected"}
            </SkyText>
          ) : null}
          {row.rebuildPending ? (
            <SkyText size={200} color="warning" testId={`report-rebuild-${row.name}`}>
              {REBUILDS_NEXT_IMPORT}
            </SkyText>
          ) : null}
        </div>
        <SkyText
          size={200}
          color="muted"
          block
          className={BREAKABLE_NAME_CLASS}
          testId={`report-table-${row.name}`}
        >
          Lands as {breakableName(table)}
        </SkyText>
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-sm">
          <dt className="text-(--color-text-muted)">Property</dt>
          <dd
            data-testid={`report-property-${row.name}`}
            title={withheld.has("propertyId") ? HIDDEN_REASON : undefined}
          >
            {property}
          </dd>
          <dt className="text-(--color-text-muted)">Fields</dt>
          <dd>
            <SkyButton
              appearance="subtle"
              size="small"
              aria-expanded={expanded}
              testId={`report-fields-${row.name}`}
              onClick={() => setExpanded(!expanded)}
            >
              {plural(dimensions.length, "dimension")}, {plural(metrics.length, "metric")}
            </SkyButton>
            {expanded ? (
              <div
                className="mt-1 flex flex-col gap-1"
                data-testid={`report-fields-list-${row.name}`}
              >
                <SkyText size={200} block className="wrap-anywhere">
                  Dimensions: {dimensions.join(", ")}
                </SkyText>
                <SkyText size={200} block className="wrap-anywhere">
                  Metrics: {metrics.join(", ")}
                </SkyText>
              </div>
            ) : null}
          </dd>
          <dt className="text-(--color-text-muted)">Filter</dt>
          <dd
            className="wrap-anywhere"
            data-testid={`report-filter-${row.name}`}
            title={withheld.has("dimensionFilter") ? HIDDEN_REASON : undefined}
          >
            {filter}
          </dd>
          <dt className="text-(--color-text-muted)">Data through</dt>
          <dd
            data-testid={`entity-data-through-${row.name}`}
            title={instantHoverTitle(row.refreshedThrough ?? row.boundResetAt, zone)}
          >
            {dataThroughText(row, zone, "short")}
          </dd>
          <dt className="text-(--color-text-muted)">Refresh</dt>
          <dd>{REPLACED_BY_DAY}</dd>
        </dl>
        {withheld.size > 0 ? (
          <SkyText size={200} color="muted" block>
            {HIDDEN_REASON}
          </SkyText>
        ) : null}
      </div>
      <div>
        <SkyMenu
          align="end"
          items={menu}
          trigger={
            <SkyButton
              appearance="subtle"
              size="small"
              icon={MoreActionsIcon}
              aria-label={`Actions for report ${row.name}`}
              testId={`report-actions-${row.name}`}
            />
          }
        />
      </div>
    </li>
  );
}
